import { useMemo } from 'react';
import { generatePath, useLocation } from 'react-router';

import { useRequiredParams } from '~/core/shared/hooks';
import { useCan } from '~/core/shared/hooks/useCan';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { hasFormLink } from '~/core/shared/utils/formLink';
import { buildFormPermission } from '~/core/shared/utils/permissions';
import { useMiddleOfficeConfig } from '../../../../../../contexts/middleOfficeConfig';
import { EditDetailsProps } from '../../types/EditDetailsProps';

export const useEditDetails = (
  title: EditDetailsProps['title'],
  blockId: EditDetailsProps['blockId'],
  documentType: EditDetailsProps['documentType'],
) => {
  const middleOfficeConfig = useMiddleOfficeConfig();
  const { pathname: currentPath } = useLocation();
  const formLinks = middleOfficeConfig.getFormLinks(documentType);
  const matchedTitle = hasFormLink(title, formLinks);
  const { uuid } = useRequiredParams(['uuid']);
  const { can } = useCan();

  // The form-update key derives mechanically from the document type
  // (forms.{type}_form.update): a type whose form permission is unseeded
  // simply never matches a grant.
  const hasEditPermission = useMemo(
    () => can(buildFormPermission(`${documentType}_form`, 'update')),
    [can, documentType],
  );

  const editLink = useMemo(() => {
    // TODO: @haxxxton.. what a mess.. just use the Path constant, this path replacing is a nightmare
    if (!matchedTitle) return null;
    let editLocation = currentPath?.replace('summary', matchedTitle.page);
    let linkParam = blockId
      ? `${matchedTitle.urlParam}-${blockId}`
      : matchedTitle.urlParam;

    if (!hasEditPermission) {
      editLocation =
        title === 'Finance details'
          ? currentPath?.replace('summary', 'rates-and-fees')
          : currentPath?.replace('summary', 'edit');
      linkParam = '';
    }
    return `${generatePath(editLocation, { uuid })}?link=${linkParam}`;
  }, [uuid, hasEditPermission, matchedTitle, currentPath, blockId, title]);

  const scrollToTop = useEventHandler(() => {
    window.scrollTo(0, 0);
  });

  return {
    editLink,
    scrollToTop,
  };
};
